import type { PayPalApproveOrderResponseProps } from '../../../types/cart';

// NEXT_PUBLIC_PIXUM_PAYMENT_HOST is injected at runtime by k8s — no stage detection needed in code.
// Must be NEXT_PUBLIC_ because this runs client-side (synchronous with PayPal SDK callbacks).
const ENDPOINT = `${process.env['NEXT_PUBLIC_PIXUM_PAYMENT_HOST']}/api/payments/paypal-express/approveOrder`;

/**
 * Sends the PayPal approve-order request directly to the payment service.
 *
 * Why raw fetch and not a Server Action:
 * Server Actions serialize return values as plain JSON, so a raw Response object
 * cannot be returned. The caller (PayPalSmartButton) needs response.ok, response.status,
 * and response.json() to distinguish between error codes (e.g. PAYPAL_INVALID_ADDRESS)
 * and decide whether to show a shipping address error or a general error. Wrapping
 * this in a Server Action would require duplicating that logic or restructuring the
 * entire handleApproveResponse flow.
 *
 * Why raw fetch and not ClientFetch.makeRequest():
 * This call is explicitly unauthenticated — the payment service authenticates via the
 * PayPal order ID itself, not via a Pixum JWT. Using ClientFetch would inject an unnecessary
 * Authorization header.
 * If the JWT is expired or missing at the time of the call — ClientFetchTransformer would attempt a token
 * refresh or anonymous login before making the request, adding latency inside a PayPal SDK callback where timing is sensitive.
 */
export async function payPalApproveOrderResponse(data: PayPalApproveOrderResponseProps): Promise<Response> {
  return fetch(ENDPOINT, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'accept-language': data.acceptLanguageHeader,
    },
    body: JSON.stringify({
      transactionId: data.transactionId,
      paypalOrderId: data.paypalOrderId,
    }),
  });
}
